import { useCallback, useEffect, useRef } from 'react';

/**
 * Таймер автозакрытия с паузой. Остаток хранится в ref: пауза по наведению
 * и по фокусу независимы, таймер идёт, только когда нет ни того, ни другого.
 */
export function useNoticeTimer(ttl: number | null, onExpire: () => void) {
  const remaining = useRef(ttl);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holds = useRef({ hover: false, focus: false });
  const expire = useRef(onExpire);
  expire.current = onExpire;

  const stop = useCallback(() => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
    if (remaining.current !== null) {
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    }
  }, []);

  const start = useCallback(() => {
    if (remaining.current === null || timer.current !== null) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(() => {
      timer.current = null;
      expire.current();
    }, remaining.current);
  }, []);

  useEffect(() => {
    start();
    return stop;
  }, [start, stop]);

  const hold = useCallback(
    (source: 'hover' | 'focus', on: boolean) => {
      holds.current[source] = on;
      if (holds.current.hover || holds.current.focus) stop();
      else start();
    },
    [start, stop],
  );

  return {
    onMouseEnter: () => hold('hover', true),
    onMouseLeave: () => hold('hover', false),
    onFocus: () => hold('focus', true),
    onBlur: () => hold('focus', false),
  };
}
